import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { LIFECYCLE_FLAG_CLASSES } from '../../lib/assetLifecyclePresentation.js'
import { useSmoothReorder } from '../../hooks/useSmoothReorder.js'
import PropTypes from 'prop-types'

const DENSITY_CLASSES = {
  compact: 'h-8 px-1.5 py-1 text-xs',
  comfortable: 'h-10 px-2 py-1.5 text-sm',
}

const ROW_HEIGHT_CLASSES = {
  compact: 'h-8',
  comfortable: 'h-10',
}

const SESSION_MARKER_CLASSES = {
  overnight: 'bg-[#8B5CF6]',
  'pre-market': 'bg-[#FF9800]',
  premarket: 'bg-[#FF9800]',
  'post-market': 'bg-[#2962FF]',
  postmarket: 'bg-[#2962FF]',
}

const FRESHNESS_LABELS = {
  live: 'Live',
  delayed: 'Delayed',
  stale: 'Stale',
  unavailable: 'Unavailable',
  disconnected: 'Disconnected',
  unentitled: 'Unentitled',
}

const DEFAULT_SORT = { mode: 'manual' }
const DEFAULT_GET_ROW_ID = row => row.id
const DEFAULT_COLUMN_WIDTH = 96
const DEFAULT_MIN_COLUMN_WIDTH = 56

function configuredWidth(column) {
  const width = Number(column.width)
  return Number.isFinite(width) && width > 0 ? width : DEFAULT_COLUMN_WIDTH
}

function boundedWidth(column, width) {
  const minimum = Number.isFinite(Number(column.minWidth)) ? Number(column.minWidth) : DEFAULT_MIN_COLUMN_WIDTH
  const maximum = Number.isFinite(Number(column.maxWidth)) ? Number(column.maxWidth) : 480
  return Math.min(maximum, Math.max(minimum, Math.round(width)))
}

function storedColumnWidths(storageKey) {
  if (!storageKey || typeof window === 'undefined' || !window.localStorage) return {}
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '{}')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function displayLabel(value) {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return null
  if (normalized === 'premarket' || normalized === 'pre-market') return 'Pre-market'
  if (normalized === 'postmarket' || normalized === 'post-market') return 'Post-market'
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

function rowDepth(row, rowsById) {
  let depth = 0
  let parentId = row?.parentId
  const visited = new Set()
  while (parentId != null && rowsById.has(String(parentId)) && !visited.has(String(parentId))) {
    visited.add(String(parentId))
    depth += 1
    parentId = rowsById.get(String(parentId))?.parentId
  }
  return depth
}

function compareValues(left, right) {
  const leftMissing = left == null || left === ''
  const rightMissing = right == null || right === ''
  if (leftMissing || rightMissing) {
    if (leftMissing && rightMissing) return 0
    return leftMissing ? 1 : -1
  }
  if (typeof left === 'number' && typeof right === 'number') return left - right
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

function preparedRows(rows, columns, sort, getRowId) {
  let orderedRows = rows
  if (sort?.mode === 'column' && sort.columnId) {
    const column = columns.find(candidate => candidate.id === sort.columnId)
    if (column) {
      const read = column.sortValue || column.accessor || (row => row[column.id])
      const direction = sort.direction === 'desc' ? -1 : 1
      orderedRows = rows
        .map((row, index) => ({ row, index }))
        .sort((left, right) => {
          const compared = compareValues(read(left.row), read(right.row))
          return compared === 0 ? left.index - right.index : compared * direction
        })
        .map(entry => entry.row)
    }
  }

  const rowsById = new Map(rows.map(row => [String(getRowId(row)), row]))
  const childrenByParent = new Map()
  for (const row of orderedRows) {
    const parentId = row.parentId == null ? null : String(row.parentId)
    if (!parentId || !rowsById.has(parentId)) continue
    const children = childrenByParent.get(parentId) || []
    children.push(row)
    childrenByParent.set(parentId, children)
  }

  const nested = []
  const visited = new Set()
  const appendFamily = (row) => {
    const rowId = String(getRowId(row))
    if (visited.has(rowId)) return
    visited.add(rowId)
    nested.push(row)
    for (const child of childrenByParent.get(rowId) || []) appendFamily(child)
  }
  for (const row of orderedRows) {
    const parentId = row.parentId == null ? null : String(row.parentId)
    if (!parentId || !rowsById.has(parentId)) appendFamily(row)
  }
  for (const row of orderedRows) appendFamily(row)
  return nested
}

function lifecycleValues(row) {
  if (Array.isArray(row.lifecycle)) return row.lifecycle.filter(Boolean)
  return row.lifecycle ? [row.lifecycle] : []
}

function MarketMarkers({ row }) {
  const quote = row.quote || null
  const sessionKey = String(quote?.session || '').trim().toLowerCase()
  const sessionClass = SESSION_MARKER_CLASSES[sessionKey]
  const marketClosed = sessionKey === 'closed'
  const isOption = String(row.assetType || '').toLowerCase() === 'option' || row.isOption === true
  return (
    <>
      {isOption && <span className="sr-only" aria-label="Option contract" />}
      {sessionClass && (
        <span
          aria-label={`${displayLabel(sessionKey)} session`}
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${sessionClass}`}
        />
      )}
      {marketClosed && (
        <span aria-label="Market closed" title="Market closed" className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#9E9E9E]" />
      )}
    </>
  )
}

MarketMarkers.propTypes = {
  row: PropTypes.object.isRequired,
}

function QuoteTruth({ quote }) {
  const freshnessKey = String(quote?.freshness || '').trim().toLowerCase()
  // Missing prices already render as dashes; do not add a loading-time badge.
  if (freshnessKey === 'unavailable') return <span className="sr-only">Quote unavailable</span>
  const normalClosure = quote?.session === 'closed' && freshnessKey === 'stale'
    && quote?.freshnessReason === 'market-closed' && quote?.available === true
  const showFreshness = freshnessKey !== 'live' && Boolean(FRESHNESS_LABELS[freshnessKey]) && !normalClosure
  if (!showFreshness) return null
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-[9px] text-inkMuted">
      <span>{FRESHNESS_LABELS[freshnessKey]}</span>
    </span>
  )
}

QuoteTruth.propTypes = {
  quote: PropTypes.shape({
    available: PropTypes.bool,
    price: PropTypes.number,
    freshness: PropTypes.string,
    freshnessReason: PropTypes.string,
    session: PropTypes.string,
  }),
}

function AssetIdentity({ row, density }) {
  const lifecycle = lifecycleValues(row)[0] || null
  const lifecycleClass = LIFECYCLE_FLAG_CLASSES[String(lifecycle || '').toLowerCase()]
  const quoteTruth = <QuoteTruth quote={row.quote} />
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      {lifecycle && lifecycleClass && (
        <span
          aria-label={`Lifecycle: ${lifecycle}`}
          title={lifecycle}
          className={`h-5 w-2 shrink-0 ${lifecycleClass}`}
          style={{ clipPath: 'polygon(0 0, 100% 0, 68% 50%, 100% 100%, 0 100%)' }}
        />
      )}
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1">
            <span className="truncate font-medium text-ink">{row.symbol || row.displayName || 'Unknown asset'}</span>
            <MarketMarkers row={row} />
            {density === 'compact' && quoteTruth}
          </div>
          {density === 'comfortable' && (
            <div className="flex min-w-0 items-center gap-2">
              {row.displayName && row.displayName !== row.symbol && (
                <span className="truncate text-[9px] text-inkMuted">{row.displayName}</span>
              )}
              {quoteTruth}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

AssetIdentity.propTypes = {
  row: PropTypes.object.isRequired,
  density: PropTypes.oneOf(['compact', 'comfortable']).isRequired,
}

function cellValue(column, row, density) {
  if (typeof column.render === 'function') return column.render(row)
  if (column.id === 'asset') return <AssetIdentity row={row} density={density} />
  const value = typeof column.accessor === 'function' ? column.accessor(row) : row[column.id]
  return value == null || value === '' ? '—' : value
}

function actionVisible(action, row) {
  return typeof action.visible === 'function' ? action.visible(row) : action.visible !== false
}

function actionDisabled(action, row) {
  return typeof action.disabled === 'function' ? action.disabled(row) : Boolean(action.disabled)
}

function actionAriaLabel(action, row, symbolLabel) {
  if (typeof action.ariaLabel === 'function') return action.ariaLabel(row)
  if (action.ariaLabel) return action.ariaLabel
  return `${action.label} for ${symbolLabel}`
}

export default function AssetListTable({
  ariaLabel = null,
  view = null,
  rows = [],
  columns,
  groups = [],
  sort = DEFAULT_SORT,
  onSortChange = null,
  density = 'compact',
  selectedRowId = null,
  onRowSelect = null,
  onRowNavigate = null,
  onVisibleRowsChange = null,
  onRowContextMenu = null,
  onRowReorder = null,
  reorderScope = '',
  rowActions = [],
  onGroupToggle = null,
  status = 'ready',
  error = null,
  emptyMessage = 'No assets in this view',
  getRowId = DEFAULT_GET_ROW_ID,
  tableClassName = '',
  containerClassName = '',
  columnWidthStorageKey = null,
}) {
  const scrollerRef = useRef(null)
  const visibleColumns = useMemo(() => columns.filter(column => !column.hidden), [columns])
  const [columnWidths, setColumnWidths] = useState(() => storedColumnWidths(columnWidthStorageKey))
  useEffect(() => {
    setColumnWidths(storedColumnWidths(columnWidthStorageKey))
  }, [columnWidthStorageKey])
  const visibleRows = useMemo(
    () => preparedRows(rows, visibleColumns, sort, getRowId),
    [rows, visibleColumns, sort, getRowId],
  )
  const rowsById = useMemo(
    () => new Map(rows.map(row => [String(getRowId(row)), row])),
    [rows, getRowId],
  )
  const familyId = row => {
    let current = row
    const seen = new Set()
    while (current.parentId != null && rowsById.has(String(current.parentId)) && !seen.has(String(current.parentId))) {
      seen.add(String(current.parentId)); current = rowsById.get(String(current.parentId))
    }
    return String(getRowId(current))
  }
  const familyGroupId = row => rowsById.get(familyId(row))?.groupId
  const reorderRows = visibleRows.filter(row => !groups.some(group => String(group.id) === String(familyGroupId(row)) && group.collapsed))
  const familyIds = [...new Set(reorderRows.map(familyId))]
  const reorder = useSmoothReorder(familyIds, next => {
    // Keep collapsed families in the saved order while moving only visible slots.
    const moving = new Set(familyIds)
    let index = 0
    const allFamilies = [...new Set(visibleRows.map(familyId))]
    const ordered = allFamilies.map(id => moving.has(id) ? next[index++] : id)
    onRowReorder?.(ordered.flatMap(id => visibleRows.filter(row => familyId(row) === id).map(row => String(getRowId(row)))))
  }, {
    disabled: !onRowReorder || status !== 'ready', scope: reorderScope, settleMs: 180,
    groupForId: id => groups.length ? rowsById.get(id)?.groupId : '',
  })
  const actionColumnVisible = rowActions.length > 0
  const columnCount = visibleColumns.length + (actionColumnVisible ? 1 : 0) + 1
  const resolvedWidths = useMemo(() => Object.fromEntries(visibleColumns.map(column => [
    column.id,
    boundedWidth(column, columnWidths[column.id] ?? configuredWidth(column)),
  ])), [visibleColumns, columnWidths])
  const tableWidth = visibleColumns.reduce((total, column) => total + resolvedWidths[column.id], 0)
    + (actionColumnVisible ? 32 : 0)

  const saveColumnWidth = (column, nextWidth) => {
    const width = boundedWidth(column, nextWidth)
    setColumnWidths(current => {
      const next = { ...current, [column.id]: width }
      if (columnWidthStorageKey && typeof window !== 'undefined' && window.localStorage) {
        try { window.localStorage.setItem(columnWidthStorageKey, JSON.stringify(next)) } catch { /* preference persistence is best effort */ }
      }
      return next
    })
  }

  const resetColumnWidth = (column) => {
    setColumnWidths(current => {
      const next = { ...current }
      delete next[column.id]
      if (columnWidthStorageKey && typeof window !== 'undefined' && window.localStorage) {
        try { window.localStorage.setItem(columnWidthStorageKey, JSON.stringify(next)) } catch { /* preference persistence is best effort */ }
      }
      return next
    })
  }

  const beginColumnResize = (event, column) => {
    if (column.resizable === false) return
    event.preventDefault()
    event.stopPropagation()
    const startX = event.clientX
    const startWidth = resolvedWidths[column.id]
    const onPointerMove = moveEvent => saveColumnWidth(column, startWidth + moveEvent.clientX - startX)
    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
    }
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
  }

  const requestSort = (column) => {
    if (!column.sortable || !onSortChange) return
    const sameColumn = sort?.mode === 'column' && sort.columnId === column.id
    if (sameColumn && sort.direction === 'desc') { onSortChange({ mode: 'manual' }); return }
    onSortChange({
      mode: 'column',
      columnId: column.id,
      direction: sameColumn && sort.direction === 'asc' ? 'desc' : 'asc',
    })
  }

  // Collect the exact rendered order, including option families and open groups.
  const renderedRows = []
  const renderRow = (row) => {
    renderedRows.push(row)
    const rowId = String(getRowId(row))
    const depth = rowDepth(row, rowsById)
    const connectedToOptionFamily = depth > 0 || rows.some(candidate => String(candidate.parentId ?? '') === rowId)
    const selected = String(selectedRowId ?? '') === rowId
    const symbolLabel = row.symbol || row.displayName || 'asset'
    const actions = rowActions.filter(action => actionVisible(action, row))

    const selectRow = () => onRowSelect?.(row)
    const navigateRow = () => onRowNavigate?.(row)
    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        navigateRow()
      } else if (event.key === ' ') {
        event.preventDefault()
        selectRow()
      }
    }

    return (
      <tr
        key={rowId}
        data-testid={`asset-list-row-${rowId}`}
        {...reorder.bind(familyId(row), rowId)}
        onPointerDown={event => {
          if (event.metaKey && !event.target.closest('button, input, a')) reorder.start(event, familyId(row))
        }}
        onClickCapture={event => reorder.consumeClick(event)}
        title={onRowReorder ? (groups.length ? 'Command-drag to reorder within this group' : 'Command-drag to reorder this list') : undefined}
        data-row-id={rowId}
        data-asset-symbol={row.originalCard?.symbol || row.symbol}
        data-depth={depth}
        data-command-highlighted={selected}
        aria-selected={selected}
        tabIndex={0}
        onClick={selectRow}
        onDoubleClick={navigateRow}
        onContextMenu={event => onRowContextMenu?.(row, event)}
        onKeyDown={handleKeyDown}
        className={`group ${ROW_HEIGHT_CLASSES[density]} border-b border-border outline-none transition-transform duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none data-[reorder-dragging=true]:bg-canvas data-[reorder-dragging=true]:shadow-lg data-[reorder-dragging=true]:cursor-grabbing hover:bg-canvas2/70 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ink/35 ${selected ? 'bg-canvas2/80 ring-1 ring-inset ring-ink/25' : ''}`}
      >
        {visibleColumns.map((column, columnIndex) => (
          <td
            data-preserve-color={['last', 'changePercent', 'extendedHours', 'pnl'].includes(column.id) ? true : undefined}
            key={column.id}
            className={`${DENSITY_CLASSES[density]} ${column.align === 'right' ? 'text-right tabular-nums' : column.align === 'center' ? 'text-center' : 'text-left'} ${column.className || ''} ${columnIndex === 0 && connectedToOptionFamily ? 'border-l-2 border-l-slate-400/70 dark:border-l-white/45' : ''}`}
          >
            {cellValue(column, row, density)}
          </td>
        ))}
        <td aria-hidden="true" className="p-0" />
        {actionColumnVisible && (
          <td className="sticky right-0 z-[1] w-8 bg-transparent p-0 text-right">
            <div className="flex justify-end gap-1">
              {actions.map(action => (
                <button
                  key={action.id}
                  type="button"
                  disabled={actionDisabled(action, row)}
                  aria-label={actionAriaLabel(action, row, symbolLabel)}
                  title={action.label}
                  onClick={(event) => {
                    event.stopPropagation()
                    action.onSelect?.(row)
                  }}
                  onDoubleClick={event => event.stopPropagation()}
                  onKeyDown={event => event.stopPropagation()}
                  className="mr-1 rounded border-0 bg-transparent p-1 text-[10px] text-inkMuted outline-none opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto focus-visible:ring-1 focus-visible:ring-ink/30 hover:text-ink disabled:cursor-not-allowed group-hover:disabled:opacity-40 [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto"
                >
                  {typeof action.icon === 'function' ? action.icon(row) : action.icon || action.label}
                </button>
              ))}
            </div>
          </td>
        )}
      </tr>
    )
  }

  const body = []
  if (groups.length > 0 && visibleRows.length > 0) {
    const configuredIds = new Set(groups.map(group => String(group.id)))
    for (const group of groups) {
      const groupRows = visibleRows.filter(row => String(familyGroupId(row) ?? '') === String(group.id))
      if (groupRows.length === 0 && group.hideWhenEmpty !== false) continue
      const collapsed = group.collapsed === true
      body.push(
        <tr key={`group-${group.id}`} className="border-b border-border bg-transparent">
          <th colSpan={columnCount} scope="rowgroup" className="h-7 px-2 text-left text-[9px] font-normal uppercase tracking-wide text-inkMuted">
            {onGroupToggle ? (
              <button
                type="button"
                aria-expanded={!collapsed}
                aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${group.label}`}
                onClick={() => onGroupToggle(String(group.id), !collapsed)}
                className="inline-flex items-center gap-2 hover:text-ink"
              >
                <span aria-hidden="true" className="text-[11px]">{collapsed ? '›' : '⌄'}</span>
                <span>{group.label}</span>
              </button>
            ) : group.label}
            {group.description && <span className="ml-2 normal-case tracking-normal text-inkMuted/70">{group.description}</span>}
          </th>
        </tr>,
      )
      if (!collapsed) body.push(...groupRows.map(renderRow))
    }
    const ungrouped = visibleRows.filter(row => !configuredIds.has(String(familyGroupId(row) ?? '')))
    body.push(...ungrouped.map(renderRow))
  } else {
    body.push(...visibleRows.map(renderRow))
  }

  let stateMessage = null
  if (status === 'loading') stateMessage = 'Loading assets…'
  else if (status === 'error') stateMessage = error?.message || 'Asset data could not be loaded'
  else if (visibleRows.length === 0) stateMessage = emptyMessage

  useLayoutEffect(() => {
    onVisibleRowsChange?.(stateMessage ? [] : renderedRows)
  })

  // Quote-driven sorting must not undo manual scrolling. Reveal only a new
  // selection, or a pending selection whose row has just become available.
  const selectedRowVisible = !stateMessage && renderedRows.some(row => String(getRowId(row)) === String(selectedRowId))
  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || selectedRowId == null || !selectedRowVisible) return
    const row = [...scroller.querySelectorAll('tr[data-row-id]')].find(node => node.dataset.rowId === String(selectedRowId))
    if (!row) return
    const viewport = scroller.getBoundingClientRect()
    const header = scroller.querySelector('thead')?.getBoundingClientRect()
    const rect = row.getBoundingClientRect()
    const top = Math.max(viewport.top, header?.bottom || viewport.top)
    if (rect.top < top) scroller.scrollTop -= top - rect.top
    else if (rect.bottom > viewport.bottom) scroller.scrollTop += rect.bottom - viewport.bottom
  }, [selectedRowId, selectedRowVisible])

  if (status === 'ready' && visibleRows.length === 0) return null

  return (
    <div ref={scrollerRef} className={`min-h-0 min-w-0 overflow-auto border border-border bg-transparent ${containerClassName}`}>
      <table
        className={`table-fixed border-collapse text-ink ${tableClassName}`}
        style={{ width: `max(100%, ${tableWidth}px)` }}
        aria-label={ariaLabel || (view?.label ? `${view.label} assets` : 'Asset list')}
      >
        <colgroup>
          {visibleColumns.map(column => <col key={column.id} style={{ width: resolvedWidths[column.id] }} />)}
          <col />
          {actionColumnVisible && <col style={{ width: 32 }} />}
        </colgroup>
        <thead className="sticky top-0 z-20 bg-canvas shadow-[0_1px_0_rgb(var(--color-border))]">
          <tr className="border-b border-border">
            {visibleColumns.map(column => {
              const activeSort = sort?.mode === 'column' && sort.columnId === column.id
              const headerContent = (
                <>
                  {column.id === 'asset' && (
                    <span
                      aria-hidden="true"
                      data-testid="asset-list-symbol-header-flag"
                      className="h-4 w-1.5 shrink-0 bg-slate-300 dark:bg-white/25"
                      style={{ clipPath: 'polygon(0 0, 100% 0, 68% 50%, 100% 100%, 0 100%)' }}
                    />
                  )}
                  {column.header}
                </>
              )
              return (
                <th
                  key={column.id}
                  scope="col"
                  aria-label={typeof column.header === 'string' ? column.header : undefined}
                  aria-sort={column.sortable ? (activeSort ? (sort.direction === 'desc' ? 'descending' : 'ascending') : 'none') : undefined}
                  className={`${DENSITY_CLASSES[density]} relative font-normal normal-case tracking-normal text-inkMuted ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'} ${column.className || ''}`}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      aria-label={`Sort by ${column.header}`}
                      title="Click to sort ascending, descending, then restore default order"
                      disabled={!onSortChange}
                      onClick={() => requestSort(column)}
                      className="inline-flex items-center gap-1 hover:text-ink"
                    >
                      {headerContent}
                      {activeSort && <span aria-hidden="true">{sort.direction === 'desc' ? '↓' : '↑'}</span>}
                    </button>
                  ) : headerContent}
                  {column.resizable !== false && (
                    <span
                      role="separator"
                      aria-label={`Resize ${column.header} column`}
                      aria-orientation="vertical"
                      aria-valuenow={resolvedWidths[column.id]}
                      tabIndex={0}
                      title="Drag to resize; double-click to reset"
                      onPointerDown={event => beginColumnResize(event, column)}
                      onDoubleClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        resetColumnWidth(column)
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
                        event.preventDefault()
                        event.stopPropagation()
                        saveColumnWidth(column, resolvedWidths[column.id] + (event.key === 'ArrowRight' ? 8 : -8))
                      }}
                      className="absolute inset-y-0 -right-1 z-10 w-2 cursor-col-resize touch-none select-none outline-none after:absolute after:inset-y-1.5 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-transparent hover:after:bg-[#2962FF] focus-visible:after:bg-[#2962FF]"
                    />
                  )}
                </th>
              )
            })}
            <th aria-hidden="true" className="p-0" />
            {actionColumnVisible && <th aria-hidden="true" className="w-8 p-0" />}
          </tr>
        </thead>
        <tbody>
          {stateMessage ? (
            <tr>
              <td
                colSpan={columnCount}
                role={status === 'error' ? 'alert' : undefined}
                className="px-3 py-8 text-center text-xs text-inkMuted"
              >
                {stateMessage}
              </td>
            </tr>
          ) : body}
        </tbody>
      </table>
    </div>
  )
}

const columnShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  header: PropTypes.node.isRequired,
  accessor: PropTypes.func,
  sortValue: PropTypes.func,
  render: PropTypes.func,
  sortable: PropTypes.bool,
  hidden: PropTypes.bool,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  className: PropTypes.string,
  width: PropTypes.number,
  minWidth: PropTypes.number,
  maxWidth: PropTypes.number,
  resizable: PropTypes.bool,
})

AssetListTable.propTypes = {
  ariaLabel: PropTypes.string,
  view: PropTypes.shape({ id: PropTypes.string, kind: PropTypes.string, label: PropTypes.string }),
  rows: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(columnShape).isRequired,
  groups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    description: PropTypes.node,
    hideWhenEmpty: PropTypes.bool,
    collapsed: PropTypes.bool,
  })),
  sort: PropTypes.shape({
    mode: PropTypes.oneOf(['column', 'manual']),
    columnId: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }),
  onSortChange: PropTypes.func,
  density: PropTypes.oneOf(['compact', 'comfortable']),
  selectedRowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRowSelect: PropTypes.func,
  onRowNavigate: PropTypes.func,
  onVisibleRowsChange: PropTypes.func,
  onRowContextMenu: PropTypes.func,
  onRowReorder: PropTypes.func,
  reorderScope: PropTypes.string,
  rowActions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    visible: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    ariaLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    onSelect: PropTypes.func,
  })),
  onGroupToggle: PropTypes.func,
  status: PropTypes.oneOf(['ready', 'loading', 'error']),
  error: PropTypes.shape({ message: PropTypes.string }),
  emptyMessage: PropTypes.node,
  getRowId: PropTypes.func,
  tableClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  columnWidthStorageKey: PropTypes.string,
}
